import { useCallback, useEffect, useState } from 'react';
import { apiService } from '../services/api';
import { USAGE_ACTIONS, EMPTY_USAGE, usageFor as readUsage } from '../utils/creditUsage';

// ── THE credits source for the whole app ─────────────────────────────────────
//
// BUG 14 — the remaining-credits number differed between Overview, Websites and
// Credits & Subscriptions, because each screen ran its own arithmetic. They all
// consume this hook now, so a discrepancy is no longer expressible.
//
// ── ONE POOL, NOT THREE ─────────────────────────────────────────────────────
// This used to sum three separate credit types — content, images and keywords.
// The server collapsed them into a single weighted ISEO pool (migration 011),
// but this hook was never moved, and the two coexisted for long enough to be
// noticed the hard way: with the legacy rows still present alongside the new
// pool row, the dashboard summed the three stale ones and displayed 3090
// credits against a real spendable balance of 1139. Nearly three times the
// truth, on the screen customers use to decide whether to buy more.
//
// The balance now comes from ONE field — `balance`, computed server-side from
// credit_lots — rather than being re-derived here. A number the client
// recomputes is a number that can disagree with the server's, and that is
// precisely the bug above.
//
// `byType`/`detail` are retained as a compatibility shim for callers not yet
// migrated; every type reports the same pooled figure rather than a third of
// it, which is truthful (one pool pays for all three actions) and keeps those
// screens from showing a made-up split.

export const CREDIT_TYPE_LABELS = { content: 'Content', images: 'Images', keywords: 'Keywords' };

const EMPTY_BY_TYPE = { content: 0, images: 0, keywords: 0 };

const EMPTY_DETAIL = {
  content: { remaining: 0, includedRemaining: 0, purchased: 0, allotment: 0, used: 0, pct: 0 },
  images: { remaining: 0, includedRemaining: 0, purchased: 0, allotment: 0, used: 0, pct: 0 },
  keywords: { remaining: 0, includedRemaining: 0, purchased: 0, allotment: 0, used: 0, pct: 0 },
};

/** Plan/purchased split and the next expiry — what the balance panel renders. */
const EMPTY_BALANCE = {
  total: 0, plan: 0, purchased: 0, nextExpiryAt: null, nextExpiryAmount: 0,
};

// The live price table, served by /stripe/credit-packages. (The "≈ N pieces"
// figure is NOT derived here — see usePieceEstimate.)
// Fetched HERE rather than by each screen so the Credits page, Overview and
// Websites card cannot end up describing the same balance by different rules.
// Null until it resolves; the helpers fall back to their shipped defaults.
const EMPTY_PRICING = { pricing: undefined, packages: [] };

export function useCreditBalances(userId) {
  const [total, setTotal] = useState(null);
  const [byType, setByType] = useState(EMPTY_BY_TYPE);
  const [detail, setDetail] = useState(EMPTY_DETAIL);
  const [usage, setUsage] = useState(EMPTY_USAGE);
  const [lots, setLots] = useState([]);
  const [balance, setBalance] = useState(EMPTY_BALANCE);
  const [rates, setRates] = useState(EMPTY_PRICING);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  // Bumped by refresh() to force a re-read after a purchase or plan change.
  const [reloadKey, setReloadKey] = useState(0);

  const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

  useEffect(() => {
    if (!userId) return undefined;
    let active = true;
    setLoading(true);

    (async () => {
      // Balances are the thing we must have; usage is enriching. Fetch them
      // together but never let a usage failure blank the balances.
      const [balancesResult, usageResult, ratesResult] = await Promise.allSettled([
        apiService.getUserCredits(userId),
        apiService.getCreditUsage(userId),
        // Advisory: the wording helpers have sane fallbacks, so a failure here
        // degrades the copy slightly rather than blanking the balance.
        // Passed the user so the prices come back at THIS account's tier —
        // subscribers pay less for the same pack. See credits.config.ts.
        apiService.getCreditPackages(userId),
      ]);

      if (ratesResult.status === 'fulfilled') {
        const d = ratesResult.value?.data || {};
        // `packages` is what is actually on sale — 60/100/200 credits at their
        // own prices. It has always been in this payload and nothing rendered
        // it, so the CMS asked to buy a raw quantity and could not show a price.
        setRates({
          pricing: d.pricing,
          packages: Array.isArray(d.packages) ? d.packages : [],
        });
      }

      if (!active) return;

      if (balancesResult.status !== 'fulfilled') {
        setTotal(0);
        setByType(EMPTY_BY_TYPE);
        setDetail(EMPTY_DETAIL);
        setUsage(EMPTY_USAGE);
        setLots([]);
        setBalance(EMPTY_BALANCE);
        setRows([]);
        setLoading(false);
        return;
      }

      const payload = balancesResult.value || {};
      const list = payload.data || [];

      // The server's figure, derived from live (unexpired) lots. Falling back to
      // the pool row keeps this working against a server that predates the
      // `balance` field, rather than rendering zero.
      const poolRow = list.find((c) => c.credit_type === 'iseo') || {};
      const srv = payload.balance || null;
      const plan = srv ? Number(srv.plan) || 0 : Number(poolRow.included_remaining) || 0;
      const purchased = srv ? Number(srv.purchased) || 0 : Number(poolRow.purchased_remaining) || 0;
      const spendable = srv ? Number(srv.total) || 0 : plan + purchased;

      setBalance({
        total: spendable,
        plan,
        purchased,
        nextExpiryAt: srv?.next_expiry_at ? new Date(srv.next_expiry_at) : null,
        nextExpiryAmount: Number(srv?.next_expiry_amount) || 0,
      });

      // ── Usage this cycle, per action ──────────────────────────────────────
      //
      // The endpoint now reports { credits, generations } per action instead of
      // a bare credit count per legacy pool key, because the pool key stopped
      // discriminating anything when the three pools merged. See the server's
      // note on /credit-usage: it counts `user_generations`, which is the only
      // table that knows what was actually made.
      //
      // Read defensively so a CMS deployed ahead of the API degrades to zeroes
      // rather than rendering NaN into the usage card.
      const cycleUsage = usageResult.status === 'fulfilled'
        ? (usageResult.value?.data?.cycle || null)
        : null;

      // Shared reader, so the hook and the card agree on how to survive a
      // payload that predates this shape.
      const usageFor = (action) => readUsage({ byAction: cycleUsage }, action);

      // Keyword lists are free, so they are not part of what was spent this
      // cycle — see USAGE_ACTIONS.
      const usageByAction = {
        content: usageFor('content'),
        images: usageFor('images'),
      };
      const used = USAGE_ACTIONS.reduce((n, a) => n + usageByAction[a].credits, 0);

      const allotment = Number(poolRow.included_allotment) || 0;
      const basis = allotment > 0 ? allotment : used + plan;
      const pct = basis > 0 ? Math.min(100, Math.round((used / basis) * 100)) : 0;

      // Compatibility shim — same pooled figure under each legacy key. See the
      // module note: a fabricated per-type split would be worse than a repeat.
      const shim = {
        remaining: spendable, includedRemaining: plan, purchased, allotment, used, pct,
      };
      setByType({ content: spendable, images: spendable, keywords: spendable });
      setDetail({ content: shim, images: shim, keywords: shim });
      setUsage({ byAction: usageByAction, totalCredits: used, allotment });
      // One entry per (source, expiry day). Absent on an API that predates this,
      // in which case the breakdown panel renders nothing rather than crashing.
      setLots(Array.isArray(payload.lots) ? payload.lots : []);
      setRows(list);
      setTotal(spendable);
      setLoading(false);
    })();

    return () => { active = false; };
  }, [userId, reloadKey]);

  return {
    total, byType, detail, balance, rows, loading, refresh,
    // What was actually spent this cycle, per action, with the counts behind
    // it — the honest answer the three duplicated cards were faking.
    usage,
    // The balance split into batches sharing an expiry date, for the breakdown.
    lots,
    pricing: rates.pricing,
    packages: rates.packages,
  };
}
